/**
 * DragSelectContainer Component
 *
 * Encapsulates rubber-band (lasso) drag-to-select behaviour.
 * Wrap any area that contains selectable `[data-asset-id]` elements.
 * The component owns the visual selection rectangle and exposes
 * `dragHighlightedIds` / `isDragging` to children via a render prop.
 */

import { useState, useCallback } from 'react';
import { useDragSelect } from '../hooks/useDragSelect';

export interface DragSelectRenderProps {
  /** IDs currently intersecting the drag rectangle (visual feedback only) */
  dragHighlightedIds: string[];
  /** Whether the primary pointer is currently down on the drag surface */
  isPointerDown: boolean;
  /** Whether a drag is in progress */
  isDragging: boolean;
}

export interface DragSelectContainerProps {
  /** Whether drag selection is enabled */
  enabled?: boolean;
  /** Called once per selected ID when the drag ends */
  onSelect: (assetId: string) => void;
  /** Called once with all selected IDs when the drag ends */
  onSelectMany?: (assetIds: string[]) => void;
  /** Render-prop that receives drag state */
  children: (props: DragSelectRenderProps) => React.ReactNode;
  /** Optional extra class on the wrapper */
  className?: string;
}

export const DragSelectContainer = ({
  enabled = true,
  onSelect,
  onSelectMany,
  children,
  className = '',
}: DragSelectContainerProps) => {
  const [dragHighlightedIds, setDragHighlightedIds] = useState<string[]>([]);

  const handleDragSelectionChange = useCallback((ids: string[]) => {
    setDragHighlightedIds(ids);
  }, []);

  const handleDragEnd = useCallback(
    (ids: string[]) => {
      setDragHighlightedIds([]);
      if (ids.length > 0) {
        if (onSelectMany) {
          onSelectMany(ids);
        } else {
          ids.forEach((id) => onSelect(id));
        }
      }
    },
    // onSelect is stored in a ref inside useDragSelect, so empty deps is safe
    [],
  );

  const { containerRef, isPointerDown, isDragging, selectionRect } = useDragSelect({
    onSelectionChange: handleDragSelectionChange,
    onDragEnd: handleDragEnd,
    enabled,
  });

  return (
    <div
      ref={containerRef}
      className={`drag-select ${isPointerDown ? 'drag-select--pointer-down' : ''} ${isDragging ? 'drag-select--active' : ''} ${className}`}
    >
      {selectionRect && (
        <div
          className="drag-select__rect"
          style={{
            top: selectionRect.top,
            left: selectionRect.left,
            width: selectionRect.width,
            height: selectionRect.height,
          }}
        />
      )}
      {children({ dragHighlightedIds, isPointerDown, isDragging })}
    </div>
  );
};
